import * as React from 'react'
import clsx from 'clsx'
import { FormControlProps } from '../FormControl'
import { SxProps } from '../sx'
import { useSelectAdvance, UseSelectAdvanceProps } from './useSelectAdvance'
import { Popover, PopoverProps } from '../Popover'
import { capitalize } from '../../utils'
import styles from './index.module.scss'
import CaretDown from '../internal/svg-icons/CaretDown'
import Search from '../internal/svg-icons/Search'
import { Typography } from '../Typography'
import { OutlinedInput } from '../OutlinedInput'
import { PaperProps } from '../Paper'
import { List, ListProps } from '../List'
import { ListItemButtonBaseProps } from '../ListItemButton'
import { InputBaseProps } from '../InputBase'
import { Box } from '../Box'
import {
  RenderListOptionItemProps,
  RenderListOptionItemSkeletonProps,
  DefaultSkeletonListOptionItem,
  DefaultListOptionItemWrapper,
} from './cellTypes'
import { NoResultFoundOverlay, NoRowsOverlay, ErrorOverlay } from './overlay'
import { useForkRef } from '../../hooks'
import Close from '../internal/svg-icons/Close'
import { IconButton } from '../IconButton'
import { SelectAllBar } from './common'

export interface SelectClasses {
  /** Styles applied to the select component `select` class. */
  select: string
  /** Styles applied to the select component if `multiple={true}`. */
  multiple: string
  /** Styles applied to the select component if `variant="filled"`. */
  filled: string
  /** Styles applied to the select component if `variant="outlined"`. */
  outlined: string
  /** Styles applied to the select component if `variant="outlinedDash"`. */
  outlinedDash: string
  /** Styles applied to the select component if `variant="standard"`. */
  standard: string
  /** State class applied to the select component `disabled` class. */
  disabled: string
  /** State class applied to the select component if `readOnly={true}`. */
  readOnly: string
  /** Styles applied to the icon component. */
  icon: string
  /** Styles applied to the icon component if the popup is open. */
  iconOpen: string
  /** Styles applied to the icon component if `variant="filled"`. */
  iconFilled: string
  /** Styles applied to the icon component if `variant="outlined"`. */
  iconOutlined: string
  /** Styles applied to the icon component if `variant="standard"`. */
  iconStandard: string
  /** State class applied to the root element if `error={true}`. */
  error: string
}

export interface SelectAdvanceInputProps<Value>
  extends UseSelectAdvanceProps<Value>,
    Omit<
      Partial<React.HTMLAttributes<HTMLInputElement>>,
      'defaultValue' | 'value' | 'size' | 'onChange'
    > {
  variant?: FormControlProps['variant']
  size?: FormControlProps['size']
  IconComponent?: React.ElementType
  error?: boolean
  sx?: SxProps
  classes?: Partial<SelectClasses>
  renderOption?: RenderListOptionItemProps<Value>['renderOption']
  renderListItem?: (props: RenderListOptionItemProps<Value>) => JSX.Element
  renderListItemSkeleton?: (
    props: RenderListOptionItemSkeletonProps
  ) => JSX.Element
  overlay?: 'noRows' | 'noResult' | 'error'
  className?: string
  placeholder?: string
  slotProps?: {
    PopperProps?: PopoverProps
    PaperProps?: PaperProps
    InputBaseProps?: InputBaseProps
    ListProps?: ListProps
    ListItemButtonProps?: ListItemButtonBaseProps
    errorOverlay?: any
    noResultsOverlay?: any
    noRowsOverlay?: any
  }
  slots?: {
    NoRowsOverlay?: React.JSXElementConstructor<any>
    NoResultOverlay?: React.JSXElementConstructor<any>
    ErrorOverlay?: React.JSXElementConstructor<any>
  }
  /**
   * Render the custom element at top of the dropdown
   */
  renderContentTop?: JSX.Element
  /**
   * Render the custom element at bottom of the dropdown
   */
  renderContentBottom?: JSX.Element
  /**
   * Hide and show - Selct All navbar
   * @default false
   */
  hideSelectNav?: boolean
  /**
   * Hide and show - search handler
   * @default false
   */
  hideSearch?: boolean
  /**
   * SelectAll - handler
   */
  onSelectAll?: (isAllSelected: boolean) => void
}

const DefaultListOptionItem = DefaultListOptionItemWrapper<any>()

function SelectAdvanceInputFn<Value>(
  props: SelectAdvanceInputProps<Value>,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const {
    variant = 'standard',
    IconComponent = CaretDown,
    readOnly,
    error,
    disabled,
    classes,
    open,
    renderOption,
    renderListItem: RenderListItem = DefaultListOptionItem,
    renderListItemSkeleton:
      RenderListItemSkeleton = DefaultSkeletonListOptionItem,
    size,
    className,
    placeholder = '',
    slotProps,
    overlay,
    slots = {
      NoRowsOverlay: NoRowsOverlay,
      NoResultOverlay: NoResultFoundOverlay,
      ErrorOverlay: ErrorOverlay,
    },
    onFetchSubRows,
    onFocus,
    onBlur,
    renderContentTop,
    renderContentBottom,
    hideSelectNav = false,
    hideSearch = false,
    multiple,
    onSelectAll,
    ...advanceSelectHookProps
  } = props
  const {
    getRootProps,
    getInputProps,
    getPaperProps,
    getOptionProps,
    getListboxProps,
    getPopupIndicatorProps,
    anchorEl,
    setAnchorEl,
    tableInstance,
    virtualRow,
    popupOpen,
    getDisplayValue,
    isEmpty,
    isRowLoading,
    getClearAll,
    getIsAllSelected,
    getSelectAll,
    clearInputValue,
    handleClose,
  } = useSelectAdvance({
    ...advanceSelectHookProps,
    multiple,
    readOnly,
    disabled,
  })

  const anchorElement = anchorEl?.parentElement as Element

  const clientWidth = React.useMemo(
    () => anchorElement?.clientWidth,
    [anchorElement]
  )

  const rootRef = useForkRef(setAnchorEl, ref)

  return (
    <React.Fragment>
      <div
        {...getRootProps()}
        onFocus={onFocus}
        className={clsx(
          [styles.select, classes?.select],
          disabled && [styles.disabled, classes?.disabled],
          readOnly && [styles.readOnly, classes?.readOnly],
          styles[`variant${capitalize(variant)}`],
          classes?.[variant],
          error && [classes?.error],
          popupOpen && styles.focused,
          className
        )}
        ref={rootRef}
        onBlur={e => {
          if (popupOpen === false) {
            //@ts-ignore
            onBlur?.(e)
          }
        }}
      >
        {/* So the vertical align positioning algorithm kicks in. */}
        {isEmpty ? (
          // notranslate needed while Google Translate will not fix zero-width space issue
          <>
            <span className="notranslate">&#8203;</span>
            <Typography className={styles.placeholder}>
              {placeholder}
            </Typography>
          </>
        ) : (
          getDisplayValue()
        )}
      </div>
      <IconComponent
        {...getPopupIndicatorProps()}
        className={clsx(
          [styles.selectIcon, classes?.icon],
          disabled && styles.disabled,
          popupOpen && [styles.open, classes?.iconOpen],
          styles[`variant${capitalize(variant)}`],
          // @ts-ignore
          classes?.[`icon${capitalize(variant)}`]
        )}
      />
      {anchorEl ? (
        <Popover
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          {...slotProps?.PopperProps}
          PaperProps={{
            ...slotProps?.PaperProps,
            ...getPaperProps(),
            className: clsx(
              styles.menuWrapper,
              slotProps?.PaperProps?.className
            ),
            style: { maxWidth: clientWidth, ...slotProps?.PaperProps?.style },
          }}
          onClose={e => {
            if (hideSearch) {
              handleClose(e as any, 'blur')
            }
          }}
          anchorEl={anchorElement}
          open={open || popupOpen}
          marginThreshold={0}
          transitionDuration={0}
        >
          <Box className={styles['renderContent--top']}>
            {renderContentTop && renderContentTop}
            {!hideSearch ? (
              <Box className={styles.selectSearch}>
                <OutlinedInput
                  {...getInputProps()}
                  placeholder="Search"
                  size={size}
                  fullWidth
                  startAdornment={<Search />}
                  endAdornment={
                    Boolean(getInputProps().value) === true ? (
                      <IconButton onClick={clearInputValue}>
                        <Close />
                      </IconButton>
                    ) : null
                  }
                  {...slotProps?.InputBaseProps}
                />
              </Box>
            ) : null}
            {!hideSelectNav && multiple ? (
              <SelectAllBar
                getClearAll={getClearAll}
                tableInstance={tableInstance}
                getIsAllSelected={getIsAllSelected}
                getSelectAll={getSelectAll}
                selectAllHandler={onSelectAll}
              />
            ) : null}
          </Box>
          {overlay ? (
            <Box className={styles.menuOverlay}>
              {overlay === 'error'
                ? React.createElement(
                    slots.ErrorOverlay!,
                    slotProps?.errorOverlay
                  )
                : overlay === 'noResult'
                  ? React.createElement(
                      slots.NoResultOverlay!,
                      slotProps?.noResultsOverlay
                    )
                  : overlay === 'noRows'
                    ? React.createElement(
                        slots.NoRowsOverlay!,
                        slotProps?.noRowsOverlay
                      )
                    : null}
            </Box>
          ) : (
            <Box className={styles.menuList} {...getListboxProps()}>
              <List
                {...slotProps?.ListProps}
                style={{
                  height: virtualRow.getTotalSize(),
                  position: 'relative',
                }}
                size={size}
              >
                {virtualRow.getVirtualItems().map(current => {
                  const row = tableInstance.getRowModel().rows[current.index]
                  const style = {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${current.start}px)`,
                  }
                  if (row) {
                    const props = getOptionProps(row, current)
                    const currentRowLoading = isRowLoading?.[row.id] ?? false
                    return (
                      <RenderListItem
                        {...props}
                        table={tableInstance}
                        key={props.key}
                        row={row}
                        renderOption={renderOption}
                        style={style}
                        virtualItem={current}
                        measureElement={virtualRow.measureElement}
                        size={size}
                        ListItemButtonProps={slotProps?.ListItemButtonProps}
                        currentRowLoading={currentRowLoading}
                        onFetchSubRows={onFetchSubRows}
                      />
                    )
                  } else {
                    return (
                      <RenderListItemSkeleton
                        virtualItem={current}
                        key={`skeleton-${current.key}`}
                        style={style}
                        measureElement={virtualRow.measureElement}
                        size={size}
                      />
                    )
                  }
                })}
              </List>
            </Box>
          )}
          {renderContentBottom && renderContentBottom}
        </Popover>
      ) : null}
    </React.Fragment>
  )
}

export const SelectAdvanceInput = React.forwardRef(SelectAdvanceInputFn) as <T>(
  props: SelectAdvanceInputProps<T>,
  ref?: React.ForwardedRef<HTMLInputElement>
) => ReturnType<typeof SelectAdvanceInputFn>
